<template>
  <div class="hotel-widget">
    <div class="widget-side">
      <form class="pepvalidate-v3 ignore-js full-width no-text-here">
        <p class="check-in">
          <label>
            <span class="text mobile-hidden">Check-In</span>
            <span class="handle">
              <svg class="ic_calendar">
                <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#ic_calendar"></use>
              </svg>
            </span>
            <input name="check_in" autocomplete="off" type="text" readonly :value="checkInDateFormatted"
                   data-dateformat="MM d"
                   :data-mindate="minCheckInDate" data-onhide="FILTER-WIDGET-DATEPICKER-HIDE"
                   data-onselect="FILTER-WIDGET-DATEPICKER-SELECT" data-range="true"
                   @click.prevent.stop="showCalendars(false)">
          </label>
        </p>
        <p class="check-out active">
          <label>
            <span class="text mobile-hidden">Check-Out</span>
            <span class="handle">
              <svg class="ic_calendar">
                <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#ic_calendar"></use>
              </svg>
            </span>
            <input name="check_out" autocomplete="off" type="text" readonly :value="checkOutDateFormatted"
                   data-dateformat="MM d"
                   :data-mindate="minCheckOutDate" data-onhide="FILTER-WIDGET-DATEPICKER-HIDE"
                   data-onselect="FILTER-WIDGET-DATEPICKER-SELECT" data-range="true"
                   @click.prevent.stop="showCalendars(true)">
          </label>
        </p>
        <p class="full-width reservation-offer-code" style="display: none;">
          <label><span class="text">Offer Code</span>
            <input data-error-msg="Please enter a valid offer code." data-regex="^[a-zA-Z0-9]+$" name="offer_code"
                   type="text" :value="offerCode" @keyup="offerCodeKeyUpHandler" @input="offerCode = $event" ref="offerCodeInput">
          </label>
        </p>
        <p class="full-width reservation-iata" style="display: none;">
          <label>
            <span class="text">IATA</span>
            <input name="iata" type="text" ref="iataInput" v-model="iata">
          </label>
        </p>
        <div class="calendar-wrapper drop-down-overlay show-loading-spinner" :class="calendarWrapperClasses"
             @click.stop>
          <component ref="calendar" v-bind:is="calendarComponent" show-special-offers load-calendar-callback=""
                     :change-dates-callback="setDatesFromCalendar" :page-id="page.ID"
                     @loaded="calendarLoaded = true" @close="calendarOpen = false"></component>
        </div>
        <div class="clearme"></div>
      </form>

      <hotel-room-call-to-action :page="page" :offer-codes="offerCodes" show-rate-details show-rate-detail-totals></hotel-room-call-to-action>

      <hotel-room-icons :room-data="roomData" :room-availability-data="roomAvailabilityData"
                        :page="page"></hotel-room-icons>
    </div>
    <div class="widget-side">
      <a v-if="roomFloorPlanUrl.length" :href="roomFloorPlanUrl" target="_blank" rel="noopener noreferrer"
         class="btn btn-dark floorplan">Floor Plan</a>
      <table class="hotel-details" role="presentation">
        <tbody>
        <tr>
          <td colspan="2">SIZE<br>{{ roomSqFt }} sq. ft.</td>
        </tr>
        <tr>
          <td>{{ bedType }}<br>{{ bedCount }}</td>
          <td>BATHS<br>{{ searchData['BATH-COUNT'] }}</td>
        </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script lang="ts">
/*!
 * Copyright (C) 2022-2025, Peppermill Inc.
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *//*
 *		2022-08-16
 *			- Refactor filter-widget.js hotel room widget related functionality to <hotel-room-details> component.
 *
 *		2023-04-03 (jhoward)
 *			- Refactor <hotel-room-details> to use TypeScript.
 *			- Show rate details in <hotel-room-details>.
 *
 *		2023-06-20 (jhoward)
 *			- Fix mobile calendar layout in <hotel-room-details>.
 *
 *		2024-08-30 (jhoward)
 *			- Remove navigation arrows for the calendar in <hotel-room-details> as they are now included in the calendar component.
 *
 *		2025-02-13 (jhoward)
 *			- Show multiple types of beds if available in <hotel-room-details>.
*/

import {ComponentPublicInstance, defineComponent, PropType} from "vue";
import HotelRoomCallToAction from "./hotelRoomCallToAction.vue";
import HotelCalendar from "./hotelCalendar.vue";
import HotelRoomIcons from "./hotelRoomIcons.vue";
import {
  FilterCache,
  HotelPage,
  HotelRoomData,
  PageListRoomSearchData,
  ReservationDataCache,
  RoomAvailabilityData
} from "./defs";
import KeyUpEvent = JQuery.KeyUpEvent;

export default defineComponent({
  name: 'hotelRoomDetails',
  components: {
    HotelCalendar,
    HotelRoomCallToAction,
    HotelRoomIcons,
  },
  props: {
    offerCodes: {
      type: Array as PropType<string[]>,
      default: () => []
    },
    searchData: {
      type: Object as PropType<PageListRoomSearchData>,
      default: () => {
        return {
          'SQ-FT-LOW': 0,
          'SQ-FT-HIGH': 0,
          'BED': '',
          'BATH-COUNT': 0
        };
      }
    },
    minCheckInDate: {
      type: String,
      required: true
    },
    minCheckOutDate: {
      type: String,
      required: true
    },
    page: {
      type: Object as PropType<HotelPage>,
      default: () => {
        return {
          ID: 0,
          TITLE: '',
          URL: '',
          IMG_SRC: '',
          IMG_FOCUS_X: 50,
          IMG_FOCUS_Y: 50
        };
      }
    },
    roomFloorPlanUrl: {
      type: String,
      default: ''
    },
    roomData: {
      type: Array as PropType<HotelRoomData[]>,
      default: () => {
        return [{
          ROOM_TITLE: '',
          ROOM_TITLE_FIXED: '',
          ROOM_SQ_FT: 0,
          ROOM_BED_TYPE: '',
          ROOM_BED_COUNT: 0,
          ROOM_BATH_COUNT: 0,
          ROOM_SMOKING: false,
          ROOM_NON_SMOKING: false,
          ROOM_ADA: false,
          BOOKING_LINK: '#'
        }];
      }
    }
  },
  mounted() {
    $(this.initialize);
  },
  data() {
    return {
      isInitialized: false,

      savedResData: {} as ReservationDataCache,

      filterCacheLabel: 'hotel-filter-widget-selections',
      savedFilterData: {} as FilterCache,

      offerCode: '',
      iata: '',

      calendarComponent: '',
      calendarCurrentMonthIndex: -1,
      calendarMonthNodes: [],

      currentUiBreakpoint: 'mobile',

      calendarOpen: false,
      calendarLoaded: false,
    };
  },
  computed: {
    checkInDateFormatted() {
      if (this.$store.state.checkInDate) {
        return this.reservationWidgetPrepareDate(this.$store.state.checkInDate);
      }

      return '';
    },
    checkOutDateFormatted() {
      if (this.$store.state.checkOutDate) {
        return this.reservationWidgetPrepareDate(this.$store.state.checkOutDate);
      }

      return '';
    },

    calendarNumberOfMonthsToShow() {
      if ('mobile' === this.currentUiBreakpoint) {
        return 1;
      }

      return 2;
    },

    roomSqFt(): string {
      if (this.searchData['SQ-FT-HIGH'] !== this.searchData['SQ-FT-LOW']) {
        return this.searchData['SQ-FT-LOW'] + '-' + this.searchData['SQ-FT-HIGH'];
      }

      return '' + this.searchData['SQ-FT-HIGH'];
    },
    bedTypes(): {[key: string]: number[]} {
      const output: {[key: string]: number[]} = {};
      this.roomData.forEach(roomTypeData => {
        if (roomTypeData.ROOM_BED_TYPE.length && roomTypeData.ROOM_BED_COUNT > 0) {
          if (!output.hasOwnProperty(roomTypeData.ROOM_BED_TYPE)) {
            output[roomTypeData.ROOM_BED_TYPE] = [];
          }
          if (-1 === output[roomTypeData.ROOM_BED_TYPE].indexOf(roomTypeData.ROOM_BED_COUNT)) {
            output[roomTypeData.ROOM_BED_TYPE].push(roomTypeData.ROOM_BED_COUNT);
          }
        }
      });

      return output;
    },
    bedCount(): string {
      const output: string[] = [];
      for (const bedType in this.bedTypes) {
        output.push(this.bedTypes[bedType].join(' or '));
      }

      return output.join(' / ');
      // return this.searchData['BED'].split('-')[0];
    },
    bedType(): string {
      const output: string[] = [];
      for (const bedType in this.bedTypes) {
        output.push(bedType);
      }

      return output.join(' / ');
      // return this.searchData['BED'].split('-')[1] || '';
    },

    calendarWrapperClasses() {
      return {
        hide: !this.calendarOpen
      };
    },

    roomAvailabilityData(): RoomAvailabilityData {
      if (this.page.ID > 0) {
        for (let i = 0, l = this.$store.state.ROOMS.length; i < l; i++) {
          if (parseInt(this.$store.state.ROOMS[i].PAGE_ID, 10) === this.page.ID) {
            return this.$store.state.ROOMS[i];
          }
        }
      }
      return {
        ADA_VARIANTS: [],
        DATA_AVAILABLE: false,
        PAGE_ID: 0,
        ROOM_TYPES: []
      };
    },
  },
  watch: {
    savedFilterData() {
      //HANDLE CHECK IN/OUT DATES
      this.setCheckInAndCheckOutDates();
    },
    offerCode() {
      this.saveOfferCodeForLater(1500);
    },

    calendarLoaded() {
      if (this.calendarLoaded) {
        requestAnimationFrame(() => {
          const calendarNode: Element | undefined = (this.$refs.calendar as ComponentPublicInstance | null)?.$el;
          if (calendarNode) {
            this.calendarMonthNodes = Array.from(calendarNode.querySelectorAll('.rate-calendar'));
            const firstMonthShownNode: HTMLElement | null = calendarNode.querySelector('.rate-calendar:not(.hide)');
            if (firstMonthShownNode) {
              this.calendarCurrentMonthIndex = this.calendarMonthNodes.indexOf(firstMonthShownNode as never);
            }
          }
        });
      }
    },

    calendarCurrentMonthIndex() {
      this.calendarMonthNodes.forEach((rateCalendarNode: HTMLElement, rateCalendarIndex: number) => {
        if (rateCalendarIndex === this.calendarCurrentMonthIndex || rateCalendarIndex === this.calendarCurrentMonthIndex + this.calendarNumberOfMonthsToShow - 1) {
          rateCalendarNode.classList.remove('hide');
        } else {
          rateCalendarNode.classList.add('hide');
        }
      });
    },
    checkInDate() {
      if (this.$store.state.checkInDate && this.$store.state.checkOutDate) {
        window.hotelRoomsVueApp.saveReservationSelections($(this.$el).data('initialized', this.isInitialized), this.$store.state.checkInDate, this.$store.state.checkOutDate);
      }
    },
    checkOutDate() {
      if (this.$store.state.checkInDate && this.$store.state.checkOutDate) {
        window.hotelRoomsVueApp.saveReservationSelections($(this.$el).data('initialized', this.isInitialized), this.$store.state.checkInDate, this.$store.state.checkOutDate);
      }
    }
  },
  methods: {
    initialize() {
      document.addEventListener('click', () => {
        this.calendarOpen = false;
      }, {passive: true});
      window.addEventListener('resize', () => {
        requestAnimationFrame(this.setCurrentUiBreakpoint);
      }, {
        passive: true
      });
      requestAnimationFrame(this.setCurrentUiBreakpoint);

      if (window.hasOwnProperty('pepvalidateInitForm')) {
        window.pepvalidateInitForm.call(this.$refs.form as HTMLFormElement);
      }

      this.getSavedReservationData();

      this.getSavedFilterData();

      this.setCheckInAndCheckOutDates();

      this.isInitialized = true;
    },
    setCurrentUiBreakpoint(): void {
      const htmlClassList = document.querySelector('html')?.classList;
      if (htmlClassList) {
        if (htmlClassList.contains('ui-mobile')) {
          this.currentUiBreakpoint = 'mobile';
        } else if (htmlClassList.contains('ui-tablet')) {
          this.currentUiBreakpoint = 'tablet';
        } else if (htmlClassList.contains('ui-desktop')) {
          this.currentUiBreakpoint = 'desktop';
        } else if (htmlClassList.contains('ui-widescreen')) {
          this.currentUiBreakpoint = 'widescreen';
        }
      }
    },
    setCheckInAndCheckOutDates(): void {
      const startQuerystringDate = window.hotelRoomsVueApp.formatYYYYMMDDToDateObject(window.pepHelperFunctions.getParameterByName('start'));
      const endQuerystringDate = window.hotelRoomsVueApp.formatYYYYMMDDToDateObject(window.pepHelperFunctions.getParameterByName('end'));

      if (this.savedResData.checkin) {
        this.$store.commit("updateCheckInDate", new Date(this.savedResData.checkin));

        if (this.savedResData.checkout) {
          this.$store.commit('updateCheckOutDate', new Date(this.savedResData.checkout));
        } else {
          this.$store.commit('updateCheckOutDate', window.hotelRoomsVueApp.addDays(this.$store.state.checkInDate, 2));
        }
      } else if (startQuerystringDate && endQuerystringDate) {
        this.$store.commit("updateCheckInDate", startQuerystringDate);
        this.$store.commit("updateCheckOutDate", endQuerystringDate);
      }
    },
    getSavedReservationData(): void {
      this.savedResData = window.hotelRoomsVueApp.getReservationSelections();
      if (this.savedResData.code) {
        this.offerCode = this.savedResData.code;
      }
      if (this.savedResData.iata) {
        this.iata = this.savedResData.iata;
      }
    },
    getSavedFilterData() {
      const cachedFilterData = window.pepHelperFunctions.getCachedData(this.filterCacheLabel, 1800000);
      if (!!cachedFilterData && cachedFilterData.DATA?.filters) {
        const filterCache: FilterCache | null = cachedFilterData.DATA.filters;
        if (filterCache) {
          for (let key in filterCache) {
            filterCache[key as keyof FilterCache]?.forEach((value: string) => {
              this.$store.commit('updateFilter', {
                key,
                value,
                selected: true
              });
            });
          }
        }
      }
    },


    reservationWidgetPrepareDate(date: Date | string | null) {
      //make sure a date was passed in
      if (typeof date !== 'object' || !(date instanceof Date)) {
        return '';
      }

      //var monthNames	=	($container.hasClass('hotel-page'))	?	window.pepDateFunctions.language.en.monthsShort	:	window.pepDateFunctions.language.en.months;
      const newDay = date.getDate().toString(10);
      const newSuffix = (newDay === '11' || newDay === '12' || newDay === '13')
        ? 'th'
        : (newDay.substr(-1, 1) === '1')
          ? 'st'
          : (newDay.substr(-1, 1) === '2')
            ? 'nd'
            : (newDay.substr(-1, 1) === '3')
              ? 'rd'
              : 'th';

      return window.pepDateFunctions.language.en.daysShort[date.getDay()] + ', ' + window.pepDateFunctions.language.en.monthsShort[date.getMonth()] + ' ' + newDay + newSuffix;
    },


    showCalendars(startOnCheckOut?: boolean) {
      if (!this.calendarOpen) {
        if ('boolean' !== typeof startOnCheckOut) {
          startOnCheckOut = false;
        }

        if ('' === this.calendarComponent) {
          this.calendarComponent = 'hotel-calendar';
        }

        this.calendarOpen = true;
      }
    },
    setDatesFromCalendar(dayCheckIn: Date | null, dayCheckOut: Date | null, initialCheckIn: Date | null, initialCheckOut: Date | null) {
      if (
        dayCheckIn
        && dayCheckOut
        && (
          dayCheckIn.getTime() !== initialCheckIn?.getTime()
          || dayCheckOut.getTime() !== initialCheckOut?.getTime()
        )
      ) {
        document.querySelector('.page-link-filter .pepvalidate-v3')?.dispatchEvent(new CustomEvent('submit', {detail: 'skipScroll'}));
      }
    },

    offerCodeKeyUpHandler(event: KeyboardEvent) {
      if (event.key === 'Enter') {
        const buttonNodes = this.$el.querySelectorAll('.non-resort-page, .filter-submit input');
        for (let i = 0, l = buttonNodes.length; i < l; i++) {
          if ('visible' === getComputedStyle(buttonNodes[i]).visibility) {
            buttonNodes[i].dispatchEvent(new Event('click'));
            break;
          }
        }
      }
    },
    saveOfferCodeForLater(timeout?: number) {
      window.hotelRoomsVueApp.saveReservationSelections($(this.$el).data('initialized', this.isInitialized), this.$store.state.checkInDate, this.$store.state.checkOutDate);
    },
  }
})
</script>

<style scoped>
.hotel-widget .cta:deep(.rate ~ div) {
  margin-top: -0.4em;
}

.hotel-widget .cta:deep(.price) {
  margin-top: 0.5em;
  font-size: 1.4em;
}

.hotel-details {
  font-size: 0.8em;
}

.hotel-widget:deep(.room_icons--container) {
  padding-top: 1em;
  padding-bottom: 1em;
}

.hotel-widget:deep(.room_icons--icon) {
  width: 3em;
  height: 3em;
}

.hotel-widget:deep(.calendar--grid) {
  display: grid;
  font-size: 0.9rem;
  grid-template-columns: repeat(var(--months-to-show),100%);
  grid-column-gap: 0.75em;
  grid-row-gap: 0.75em;
  padding: 0.75em;
  position: relative;
}
</style>